// The objects a sheet holds (paper space): a model viewport, a schematic box,
// line, connection, image or paper dimension, and a placed view of the electrical
// schematic. One
// adapter per kind says how it is listed, outlined, moved, mirrored, copied and
// let go of, and every site that acts on the selected sheet objects — select all,
// the marquee, a drag, Arrange, Flip, copy, paste, delete, z-order — loops over
// SHEET_KINDS through it, so a new kind does not compile until it says how it
// does each (docs/claude/sheets.md, *Sheet objects*).
// Paper mm, Y down. DOM-free (unit-tested).

import type {
  ElecView,
  SchematicBox,
  SchematicConn,
  SchematicContent,
  SchematicDim,
  SchematicImage,
  SchematicLine,
  Sheet,
  SheetViewport,
  Vec2,
} from '../shared/types';
import {
  paperDimFigure,
  paperDimOffset,
} from './paperDim';

export type SheetKind =
  | 'box'
  | 'conn'
  | 'dim'
  | 'elec'
  | 'image'
  | 'line'
  | 'viewport';

/** A sheet object, by its kind and id (an electrical view by its view id). */
export interface SchemSel { kind: SheetKind; id: string }

/** Every kind, in the order a sheet paints them: what Select All and the marquee
 *  gather (the last is on top, the primary of what they take). */
export const SHEET_KINDS: readonly SheetKind[] = ['viewport', 'elec', 'image', 'line', 'conn', 'box', 'dim'];

export interface Rect { x: number; y: number; w: number; h: number }
type ObjOf = { box: SchematicBox; conn: SchematicConn; dim: SchematicDim; elec: ElecView; image: SchematicImage; line: SchematicLine; viewport: SheetViewport };
export type SheetObject = ObjOf[SheetKind];

/** What the adapters need from outside: an electrical view's frame, worked out
 *  from the drawing (elecLayout.ts). */
export interface SheetServices { elecBounds(v: ElecView): Rect }

export interface SheetObjectAdapter<T> {
  /** The sheet's objects of this kind in paint order — the array a z-order
   *  restacks; created empty when `make` (else a fresh empty one, unattached). */
  of(sh: Sheet, make?: boolean): T[];
  /** Writes the kind's objects back (after a delete). */
  set(sh: Sheet, objs: T[]): void;
  /** Its outline: a frame closed, a path open. */
  path(sh: Sheet, o: T): { ring: Vec2[]; closed: boolean } | null;
  /** Its own points, with no sheet to read (a copy's extent): a connection's
   *  bends only — its ends follow its boxes. */
  points(o: T): Vec2[];
  /** A drag moves it by dx, dy: what it points at stays (a box's leader tip, a
   *  connection's ends). */
  move(o: T, dx: number, dy: number): void;
  /** A copy lands dx, dy away: all of it goes (a box's leader tip too). */
  shift(o: T, dx: number, dy: number): void;
  /** Mirrored across x = c ('h') or y = c ('v'); none: Flip leaves it as it is,
   *  and a selection of those alone flips nothing (records no step). */
  mirror?(sh: Sheet, o: T, axis: 'h' | 'v', c: number): void;
  /** Whether Arrange and Flip take it (a connection follows its boxes). */
  arranged: boolean;
  /** Its rectangle, for the kinds a resize handle sizes. */
  rect?(o: T): Rect;
  /** Points a new point on the page snaps to. */
  snapPoints?(o: T): Vec2[];
  /** A copy's id prefix. */
  prefix: string;
  /** A copy rebound to what was copied with it (`ids`, old → new); false drops it
   *  — a connection whose boxes are on neither side. */
  relink?(copy: T, ids: ReadonlyMap<string, string>, sh: Sheet): boolean;
  /** Whether it still stands once others went (a connection needs both boxes). */
  stands?(o: T, sh: Sheet): boolean;
  /** Held where it is: not moved, arranged or deleted (a locked viewport). */
  fixed?(o: T): boolean;
}
export type SheetAdapters = { [K in SheetKind]: SheetObjectAdapter<ObjOf[K]> };

/** A sheet's schematic content, created empty when absent. */
export function schContent(sh: Sheet): SchematicContent {
  return (sh.schematic ??= { boxes: [], lines: [], conns: [], images: [] });
}

const corners = (r: Rect): Vec2[] => [{ x: r.x, y: r.y }, { x: r.x + r.w, y: r.y }, { x: r.x + r.w, y: r.y + r.h }, { x: r.x, y: r.y + r.h }];
const frame = (r: Rect | null | undefined): { ring: Vec2[]; closed: boolean } | null => (r ? { ring: corners(r), closed: true } : null);
const flipPt = (q: Vec2, axis: 'h' | 'v', c: number): void => { if (axis === 'h') q.x = 2 * c - q.x; else q.y = 2 * c - q.y; };
const flipRect = (r: Rect, axis: 'h' | 'v', c: number): void => { if (axis === 'h') r.x = 2 * c - r.x - r.w; else r.y = 2 * c - r.y - r.h; };
const movePts = (pts: Vec2[], dx: number, dy: number): Vec2[] => pts.map((p) => ({ x: p.x + dx, y: p.y + dy }));

/** Centre of a box. */
export function schBoxCenter(b: SchematicBox): Vec2 { return { x: b.x + b.w / 2, y: b.y + b.h / 2 }; }

/** The point on a box's border along the ray toward `toward`, so a connection
 *  meets the edge rather than the centre. */
export function schBoxAnchor(b: SchematicBox, toward: Vec2): Vec2 {
  const c = schBoxCenter(b);
  const dx = toward.x - c.x, dy = toward.y - c.y;
  if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) return c;
  const hw = b.w / 2, hh = b.h / 2;
  const tx = dx !== 0 ? hw / Math.abs(dx) : Infinity;
  const ty = dy !== 0 ? hh / Math.abs(dy) : Infinity;
  const t = Math.min(tx, ty);
  return { x: c.x + dx * t, y: c.y + dy * t };
}

/** A connection's route: [anchorFrom, …waypoints, anchorTo]; null when either
 *  box is missing. */
export function schConnRoute(sh: Sheet, c: SchematicConn): Vec2[] | null {
  const boxes = sh.schematic?.boxes ?? [];
  const a = boxes.find((b) => b.id === c.from);
  const b = boxes.find((x) => x.id === c.to);
  if (!a || !b) return null;
  const wps = c.waypoints ?? [];
  const firstToward = wps[0] ?? schBoxCenter(b);
  const lastToward = wps[wps.length - 1] ?? schBoxCenter(a);
  return [schBoxAnchor(a, firstToward), ...wps, schBoxAnchor(b, lastToward)];
}

/** An electrical view moved: its grid, and its table and legend where placed by hand. */
export function elecShiftView(v: ElecView, dx: number, dy: number): void {
  v.x += dx; v.y += dy;
  if (v.table) { v.table.x += dx; v.table.y += dy; }
  if (v.legend) { v.legend.x += dx; v.legend.y += dy; }
}

/** The adapters, one per kind. */
export function sheetObjects(svc: SheetServices): SheetAdapters {
  const rectKind = <T extends SchematicBox | SchematicImage>(key: 'boxes' | 'images', prefix: string, leader: boolean): SheetObjectAdapter<T> => ({
    of: (sh, make) => ((make ? schContent(sh) : sh.schematic)?.[key] ?? []) as T[],
    set: (sh, objs) => { (schContent(sh) as unknown as Record<string, T[]>)[key] = objs; },
    path: (_sh, o) => frame(o),
    points: (o) => corners(o),
    move: (o, dx, dy) => { o.x += dx; o.y += dy; },
    shift: (o, dx, dy) => {
      o.x += dx; o.y += dy;
      const b = o as SchematicBox;
      if (leader && b.leader) b.leader = { x: b.leader.x + dx, y: b.leader.y + dy };
    },
    mirror: (_sh, o, axis, c) => { flipRect(o, axis, c); const b = o as SchematicBox; if (leader && b.leader) flipPt(b.leader, axis, c); },
    arranged: true,
    rect: (o) => ({ x: o.x, y: o.y, w: o.w, h: o.h }),
    snapPoints: (o) => corners(o),
    prefix,
  });
  return {
    box: rectKind<SchematicBox>('boxes', 'box', true),
    image: rectKind<SchematicImage>('images', 'img', false),
    line: {
      of: (sh, make) => (make ? schContent(sh) : sh.schematic)?.lines ?? [],
      set: (sh, objs) => { schContent(sh).lines = objs; },
      path: (_sh, o) => ({ ring: o.points, closed: false }),
      points: (o) => o.points,
      move: (o, dx, dy) => { o.points = movePts(o.points, dx, dy); },
      shift: (o, dx, dy) => { o.points = movePts(o.points, dx, dy); },
      mirror: (_sh, o, axis, c) => o.points.forEach((q) => flipPt(q, axis, c)),
      arranged: true,
      snapPoints: (o) => o.points,
      prefix: 'line',
    },
    conn: {
      of: (sh, make) => (make ? schContent(sh) : sh.schematic)?.conns ?? [],
      set: (sh, objs) => { schContent(sh).conns = objs; },
      path: (sh, o) => { const r = schConnRoute(sh, o); return r ? { ring: r, closed: false } : null; },
      points: (o) => o.waypoints ?? [],
      move: (o, dx, dy) => { if (o.waypoints) o.waypoints = movePts(o.waypoints, dx, dy); },
      shift: (o, dx, dy) => { if (o.waypoints) o.waypoints = movePts(o.waypoints, dx, dy); },
      mirror: (_sh, o, axis, c) => o.waypoints?.forEach((q) => flipPt(q, axis, c)),
      arranged: false,
      prefix: 'conn',
      // Its ends rebind to the copied boxes, else to the boxes on the target sheet.
      relink: (copy, ids, sh) => {
        copy.from = ids.get(copy.from) ?? copy.from;
        copy.to = ids.get(copy.to) ?? copy.to;
        const boxes = sh.schematic?.boxes ?? [];
        return boxes.some((b) => b.id === copy.from) && boxes.some((b) => b.id === copy.to);
      },
      stands: (o, sh) => { const boxes = sh.schematic?.boxes ?? []; return boxes.some((b) => b.id === o.from) && boxes.some((b) => b.id === o.to); },
    },
    dim: {
      of: (sh, make) => (make ? (schContent(sh).dims ??= []) : (sh.schematic?.dims ?? [])),
      set: (sh, objs) => { schContent(sh).dims = objs; },
      path: (sh, o) => { const f = paperDimFigure(o, sh.viewports); return f ? { ring: [o.a, f.geom.a, f.geom.b, o.b], closed: false } : null; },
      points: (o) => [o.a, o.b],
      move: (o, dx, dy) => { o.a = { x: o.a.x + dx, y: o.a.y + dy }; o.b = { x: o.b.x + dx, y: o.b.y + dy }; },
      shift: (o, dx, dy) => { o.a = { x: o.a.x + dx, y: o.a.y + dy }; o.b = { x: o.b.x + dx, y: o.b.y + dy }; },
      // Its line stays on the side it was mirrored to.
      mirror: (sh, o, axis, c) => {
        const f = paperDimFigure(o, sh.viewports);
        const at = f ? { x: (f.geom.a.x + f.geom.b.x) / 2, y: (f.geom.a.y + f.geom.b.y) / 2 } : { ...o.a };
        flipPt(o.a, axis, c); flipPt(o.b, axis, c); flipPt(at, axis, c);
        o.offset = paperDimOffset(o.a, o.b, at, o.kind);
      },
      arranged: true,
      prefix: 'dim',
    },
    elec: {
      of: (sh, make) => (make ? (sh.elecViews ??= []) : (sh.elecViews ?? [])),
      set: (sh, objs) => { sh.elecViews = objs; },
      path: (_sh, o) => frame(svc.elecBounds(o)),
      points: (o) => corners(svc.elecBounds(o)),
      move: (o, dx, dy) => elecShiftView(o, dx, dy),
      shift: (o, dx, dy) => elecShiftView(o, dx, dy),
      // No mirror: a schematic's view is not mirrored, its text must read.
      arranged: true,
      prefix: 'elec',
    },
    // A viewport's frame moves; the model in it is not mirrored (no mirror). Its own handles
    // resize it (sheets.ts), so it has no `rect` here.
    viewport: {
      of: (sh) => sh.viewports,
      set: (sh, objs) => { sh.viewports = objs; },
      path: (_sh, o) => frame(o),
      points: (o) => corners(o),
      move: (o, dx, dy) => { o.x += dx; o.y += dy; },
      shift: (o, dx, dy) => { o.x += dx; o.y += dy; },
      arranged: true,
      snapPoints: (o) => corners(o),
      prefix: 'vp',
      // One saved without the flag reads locked.
      fixed: (o) => o.locked !== false,
    },
  };
}

// ---- Acting on several -------------------------------------------------------------

/** Loose access to one kind's adapter (the kinds are distinct object types). */
const loose = (A: SheetAdapters, k: SheetKind): SheetObjectAdapter<SheetObject> => A[k] as unknown as SheetObjectAdapter<SheetObject>;

/** The object a selection names, if it is still on the sheet. */
export function sheetObject(sh: Sheet, A: SheetAdapters, s: SchemSel): SheetObject | undefined {
  return loose(A, s.kind).of(sh).find((o) => o.id === s.id);
}

/** Whether an object is held where it is (a locked viewport). */
export function isFixed(A: SheetAdapters, kind: SheetKind, o: SheetObject): boolean {
  return loose(A, kind).fixed?.(o) ?? false;
}

/** Every object on the sheet, in paint order. */
export function allSheetObjects(sh: Sheet, A: SheetAdapters): SchemSel[] {
  return SHEET_KINDS.flatMap((kind) => loose(A, kind).of(sh).map((o) => ({ kind, id: o.id })));
}

/** An object's outline. */
export function sheetObjectPath(sh: Sheet, A: SheetAdapters, s: SchemSel): { ring: Vec2[]; closed: boolean } | null {
  const o = sheetObject(sh, A, s);
  return o ? loose(A, s.kind).path(sh, o) : null;
}

/** The objects as they stand now — what a drag moves from. */
export interface SheetSnapshot { sel: SchemSel; obj: SheetObject }
export function snapshotSheetObjects(sh: Sheet, A: SheetAdapters, sels: readonly SchemSel[]): SheetSnapshot[] {
  return sels.flatMap((sel) => { const o = sheetObject(sh, A, sel); return o ? [{ sel, obj: structuredClone(o) }] : []; });
}
/** Each object put back as it stood, then moved by dx, dy — a drag's frame. */
export function moveFromSnapshot(sh: Sheet, A: SheetAdapters, snap: readonly SheetSnapshot[], dx: number, dy: number): void {
  for (const { sel, obj } of snap) {
    const o = sheetObject(sh, A, sel);
    if (!o || isFixed(A, sel.kind, o)) continue;
    Object.assign(o, structuredClone(obj));
    loose(A, sel.kind).move(o, dx, dy);
  }
}

/** What Arrange and Flip take: each object's box (paper mm, Y down) and its move. */
export function arrangeSheetObjects(sh: Sheet, A: SheetAdapters, sels: readonly SchemSel[]): Array<{ sel: SchemSel; box: { min: Vec2; max: Vec2 }; move: (dx: number, dy: number) => void }> {
  const out: Array<{ sel: SchemSel; box: { min: Vec2; max: Vec2 }; move: (dx: number, dy: number) => void }> = [];
  for (const sel of sels) {
    const a = loose(A, sel.kind), o = sheetObject(sh, A, sel);
    if (!o || !a.arranged || isFixed(A, sel.kind, o)) continue;
    const pts = sel.kind === 'dim' || sel.kind === 'line' ? a.points(o) : a.path(sh, o)?.ring ?? [];
    if (!pts.length) continue;
    const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
    out.push({ sel, box: { min: { x: Math.min(...xs), y: Math.min(...ys) }, max: { x: Math.max(...xs), y: Math.max(...ys) } }, move: (dx, dy) => a.move(o, dx, dy) });
  }
  return out;
}

/** Whether Flip changes anything: some object it takes is one it mirrors (a view
 *  or a viewport alone is not, nor a connection alone, which Flip does not take). */
export function sheetObjectsFlip(sh: Sheet, A: SheetAdapters, sels: readonly SchemSel[]): boolean {
  return arrangeSheetObjects(sh, A, sels).some((t) => !!loose(A, t.sel.kind).mirror);
}

/** The objects mirrored about their common box's middle (their arranged boxes);
 *  false, and nothing changed, when Flip changes nothing (sheetObjectsFlip). */
export function mirrorSheetObjects(sh: Sheet, A: SheetAdapters, sels: readonly SchemSel[], axis: 'h' | 'v'): boolean {
  const t = arrangeSheetObjects(sh, A, sels);
  if (!sheetObjectsFlip(sh, A, sels)) return false;
  const lo = Math.min(...t.map((x) => (axis === 'h' ? x.box.min.x : x.box.min.y)));
  const hi = Math.max(...t.map((x) => (axis === 'h' ? x.box.max.x : x.box.max.y)));
  const c = (lo + hi) / 2;
  for (const sel of sels) { const o = sheetObject(sh, A, sel); if (o && !isFixed(A, sel.kind, o)) loose(A, sel.kind).mirror?.(sh, o, axis, c); }
  return true;
}

/** A copied sheet object, detached from its sheet. */
export interface SheetClipItem { kind: SheetKind; obj: SheetObject }
export function copySheetObjects(sh: Sheet, A: SheetAdapters, sels: readonly SchemSel[]): SheetClipItem[] {
  return sels.flatMap((s) => { const o = sheetObject(sh, A, s); return o ? [{ kind: s.kind, obj: structuredClone(o) }] : []; });
}

/** The box (paper mm) copied objects cover. */
export function sheetClipBounds(A: SheetAdapters, items: readonly SheetClipItem[]): { min: Vec2; max: Vec2 } | null {
  const pts = items.flatMap((it) => loose(A, it.kind).points(it.obj));
  if (!pts.length) return null;
  return {
    min: { x: Math.min(...pts.map((p) => p.x)), y: Math.min(...pts.map((p) => p.y)) },
    max: { x: Math.max(...pts.map((p) => p.x)), y: Math.max(...pts.map((p) => p.y)) },
  };
}

/** Copies added to the sheet dx, dy away, each with a fresh id (`uid(prefix)`):
 *  the kinds others bind to first, so a copied connection rebinds to the copies of
 *  its boxes; one that cannot is left out. Returns what was added. */
export function pasteSheetObjects(sh: Sheet, A: SheetAdapters, items: readonly SheetClipItem[], dx: number, dy: number, uid: (prefix: string) => string): SchemSel[] {
  const ids = new Map<string, string>();
  const placed: Array<{ kind: SheetKind; obj: SheetObject }> = [];
  for (const it of items) {
    const copy = structuredClone(it.obj), a = loose(A, it.kind);
    ids.set(copy.id, (copy.id = uid(a.prefix)));
    a.shift(copy, dx, dy);
    placed.push({ kind: it.kind, obj: copy });
  }
  const out: SchemSel[] = [];
  for (const pass of [false, true]) {
    for (const { kind, obj } of placed) {
      const a = loose(A, kind);
      if (!!a.relink !== pass) continue;
      if (a.relink && !a.relink(obj, ids, sh)) continue;
      a.of(sh, true).push(obj);
      out.push({ kind, id: obj.id });
    }
  }
  return out;
}

/** What of `sels` a delete takes: not what is held (a locked viewport). */
export function deletableSheetObjects(sh: Sheet, A: SheetAdapters, sels: readonly SchemSel[]): SchemSel[] {
  return sels.filter((s) => { const o = sheetObject(sh, A, s); return !!o && !isFixed(A, s.kind, o); });
}

/** The objects taken off the sheet — what is held stays —, and what stood on
 *  them (a connection on a deleted box). */
export function deleteSheetObjects(sh: Sheet, A: SheetAdapters, sels: readonly SchemSel[]): void {
  const take = deletableSheetObjects(sh, A, sels);
  for (const kind of SHEET_KINDS) {
    const gone = new Set(take.filter((s) => s.kind === kind).map((s) => s.id));
    const a = loose(A, kind), list = a.of(sh);
    if (gone.size && list.length) a.set(sh, list.filter((o) => !gone.has(o.id)));
  }
  for (const kind of SHEET_KINDS) {
    const a = loose(A, kind);
    if (a.stands && a.of(sh).some((o) => !a.stands!(o, sh))) a.set(sh, a.of(sh).filter((o) => a.stands!(o, sh)));
  }
}

/** Points on the sheet's objects a new point snaps to. */
export function sheetSnapPoints(sh: Sheet, A: SheetAdapters): Vec2[] {
  return SHEET_KINDS.flatMap((kind) => { const a = loose(A, kind); return a.snapPoints ? a.of(sh).flatMap((o) => a.snapPoints!(o)) : []; });
}
